/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import firebase from 'firebase/app'
import EventEmitter from './utilities/EventEmitter'
import FirebaseUtils from './utilities/FirebaseUtils'
import constants from './constants'
import Environment from './utilities/Environment'
import AppState, { AppStateType } from './utilities/AppState'

/**
 * Manages interacting with the global server data.
 * @event updated When data changes
 */
class ServerDimension extends EventEmitter {

    /** Dimension ID */
    id = 'default'

    /** Link back to the Server */
    server = null

    /** Server data */
    data = {}

    /** True if loaded */
    loaded = false

    /** Default values, used if nothing is set on the server */
    defaults = {

        // Allows any user to unmute their microphone
        allow_user_unmute: true,

        // Displays signal strength indicator, which visualises connection strength to spaces (when `true`)
        show_signal_strength: false,

        // Allows users to control their audio and video contexts independently (when `true`)
        // allow_context_options: true,

        // Inactivity Timer Length (default 3 min)
        // Notice: have restricted this to not be shorter than 30 seconds so that it doesnt conflict with the final popup timer (which is 15 seconds)
        inactivityTimerLength: 180000,

        // Allow rotation with arrow keys
        controls_rotate_with_arrows: true,

        // Color theme
        menuColor: '#141414',
        editorColor: '#202020',
        sectionColor: '#7B8082',
        fieldColor: '#FFFFFF',

        // Effects
        effect_outline: true,

        // Spawn radius
        spawnRadius: 0,

        // Hide Side Bar Color
        hideSideBarColor: true,

        // "Leave" button in either the bottom-right (desktop) or top-left (mobile) corner of the screen
        show_leave_button: true,

        // Unsupported browser handling
        unsupported_browser_detect: false,
        unsupported_browser_action: 'warning',
        unsupported_browser_message: 'You are using an unsupported browser and may experience some issues. We recommend using the Google Chrome browser for the best experience.',

        user_locked: "",

        last_auto_issue_check: Date.now(),

        // Overrides default starting view
        start_view_override: 'none',
        start_view_override_mobile: 'none',

        // Default zoom levels
        minZoom: constants.view.minZoom,
        maxZoom: constants.view.maxZoom,

        // Loading screen information for going to another space from Places plugin
        places_loading_show_screen: true,
        places_loading_show_image: true,
        places_loading_show_text: false,
        places_loading_text: 'Teleporting you to another space...',

        fps_warning_enabled: true,

        only_allow_business_vatoms: true,

        // Global chat channels
        global_channels: [
            {
                id: 'everyone',
                name: 'General Chat',
                description: 'Chat to everyone within the space',
                is_group: true,
                from_system: true,
                image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iMTEuNSIgZmlsbD0iIzAwNzRDQyIvPgo8cGF0aCBkPSJNMTguMyAxMS4xNjkyQzE5LjUzNzUgMTEuMTY5MiAyMC41NSAxMC4xODQ4IDIwLjU1IDguOTE5MjNDMjAuNTUgNy42ODE3MyAxOS41Mzc1IDYuNjY5MjMgMTguMyA2LjY2OTIzQzE3LjAzNDQgNi42NjkyMyAxNi4wNSA3LjY4MTczIDE2LjA1IDguOTE5MjNDMTYuMDUgMTAuMTg0OCAxNy4wMzQ0IDExLjE2OTIgMTguMyAxMS4xNjkyWk0xOC4zIDcuNTY5MjNDMTkuMDMxMiA3LjU2OTIzIDE5LjY1IDguMTg3OTggMTkuNjUgOC45MTkyM0MxOS42NSA5LjY3ODYgMTkuMDMxMiAxMC4yNjkyIDE4LjMgMTAuMjY5MkMxNy41NDA2IDEwLjI2OTIgMTYuOTUgOS42Nzg2IDE2Ljk1IDguOTE5MjNDMTYuOTUgOC4xODc5OCAxNy41NDA2IDcuNTY5MjMgMTguMyA3LjU2OTIzWk0xMiAxMi4wNjkyQzEzLjcxNTYgMTIuMDY5MiAxNS4xNSAxMC42NjMgMTUuMTUgOC45MTkyM0MxNS4xNSA3LjIwMzYgMTMuNzE1NiA1Ljc2OTIzIDEyIDUuNzY5MjNDMTAuMjU2MiA1Ljc2OTIzIDguODUgNy4yMDM2IDguODUgOC45MTkyM0M4Ljg1IDEwLjY2MyAxMC4yNTYyIDEyLjA2OTIgMTIgMTIuMDY5MlpNMTIgNi42NjkyM0MxMy4yMzc1IDYuNjY5MjMgMTQuMjUgNy42ODE3MyAxNC4yNSA4LjkxOTIzQzE0LjI1IDEwLjE4NDggMTMuMjM3NSAxMS4xNjkyIDEyIDExLjE2OTJDMTAuNzM0NCAxMS4xNjkyIDkuNzUgMTAuMTg0OCA5Ljc1IDguOTE5MjNDOS43NSA3LjY4MTczIDEwLjczNDQgNi42NjkyMyAxMiA2LjY2OTIzWk0xOC44NjI1IDEyLjA2OTJIMTcuNzM3NUMxNy4yODc1IDEyLjA2OTIgMTYuODkzOCAxMi4yMDk4IDE2LjU1NjIgMTIuNDM0OEMxNi44Mzc1IDEyLjYzMTcgMTcuMDYyNSAxMi44Mjg2IDE3LjI4NzUgMTMuMDgxN0MxNy40MjgxIDEzLjAyNTUgMTcuNTY4OCAxMi45NjkyIDE3LjczNzUgMTIuOTY5MkgxOC44NjI1QzE5LjUzNzUgMTIuOTY5MiAyMC4xIDEzLjU4OCAyMC4xIDE0LjMxOTJDMjAuMSAxNC41NzIzIDIwLjI5NjkgMTQuNzY5MiAyMC41NSAxNC43NjkyQzIwLjc3NSAxNC43NjkyIDIxIDE0LjU3MjMgMjEgMTQuMzE5MkMyMSAxMy4wODE3IDIwLjAxNTYgMTIuMDY5MiAxOC44NjI1IDEyLjA2OTJaTTUuNyAxMS4xNjkyQzYuOTM3NSAxMS4xNjkyIDcuOTUgMTAuMTg0OCA3Ljk1IDguOTE5MjNDNy45NSA3LjY4MTczIDYuOTM3NSA2LjY2OTIzIDUuNyA2LjY2OTIzQzQuNDM0MzggNi42NjkyMyAzLjQ1IDcuNjgxNzMgMy40NSA4LjkxOTIzQzMuNDUgMTAuMTg0OCA0LjQzNDM4IDExLjE2OTIgNS43IDExLjE2OTJaTTUuNyA3LjU2OTIzQzYuNDMxMjUgNy41NjkyMyA3LjA1IDguMTg3OTggNy4wNSA4LjkxOTIzQzcuMDUgOS42Nzg2IDYuNDMxMjUgMTAuMjY5MiA1LjcgMTAuMjY5MkM0Ljk0MDYyIDEwLjI2OTIgNC4zNSA5LjY3ODYgNC4zNSA4LjkxOTIzQzQuMzUgOC4xODc5OCA0Ljk0MDYyIDcuNTY5MjMgNS43IDcuNTY5MjNaTTE0LjI1IDEyLjYzMTdDMTMuMjkzNyAxMi42MzE3IDEzLjA2ODcgMTIuOTY5MiAxMiAxMi45NjkyQzEwLjkwMzEgMTIuOTY5MiAxMC42NzgxIDEyLjYzMTcgOS43MjE4OCAxMi42MzE3QzguNzA5MzcgMTIuNjMxNyA3LjcyNSAxMy4xMDk4IDcuMTM0MzcgMTMuOTUzNkM2Ljc5Njg4IDE0LjQ4OCA2LjYgMTUuMTA2NyA2LjYgMTUuNzgxN1YxNy4wMTkyQzYuNiAxNy43Nzg2IDcuMTkwNjIgMTguMzY5MiA3Ljk1IDE4LjM2OTJIMTYuMDVDMTYuNzgxMiAxOC4zNjkyIDE3LjQgMTcuNzc4NiAxNy40IDE3LjAxOTJWMTUuNzgxN0MxNy40IDE1LjEwNjcgMTcuMTc1IDE0LjQ4OCAxNi44Mzc1IDEzLjk1MzZDMTYuMjQ2OSAxMy4xMDk4IDE1LjI2MjUgMTIuNjMxNyAxNC4yNSAxMi42MzE3Wk0xNi41IDE3LjAxOTJDMTYuNSAxNy4yNzIzIDE2LjI3NSAxNy40NjkyIDE2LjA1IDE3LjQ2OTJINy45NUM3LjY5Njg3IDE3LjQ2OTIgNy41IDE3LjI3MjMgNy41IDE3LjAxOTJWMTUuNzgxN0M3LjUgMTUuMzAzNiA3LjYxMjUgMTQuODUzNiA3Ljg5Mzc1IDE0LjQ1OThDOC4yNTkzNyAxMy44OTczIDguOTYyNSAxMy41MzE3IDkuNzIxODggMTMuNTMxN0MxMC41MDk0IDEzLjUzMTcgMTAuNzkwNiAxMy44NjkyIDEyIDEzLjg2OTJDMTMuMjA5NCAxMy44NjkyIDEzLjQ2MjUgMTMuNTMxNyAxNC4yNSAxMy41MzE3QzE1LjAwOTQgMTMuNTMxNyAxNS43MTI1IDEzLjg5NzMgMTYuMDc4MSAxNC40NTk4QzE2LjM1OTQgMTQuODUzNiAxNi40NzE5IDE1LjMwMzYgMTYuNDcxOSAxNS43ODE3VjE3LjAxOTJIMTYuNVpNNy40MTU2MiAxMi40MzQ4QzcuMDc4MTIgMTIuMjA5OCA2LjY4NDM4IDEyLjA2OTIgNi4yNjI1IDEyLjA2OTJINS4xMzc1QzMuOTU2MjUgMTIuMDY5MiAzIDEzLjA4MTcgMyAxNC4zMTkyQzMgMTQuNTcyMyAzLjE5Njg3IDE0Ljc2OTIgMy40NSAxNC43NjkyQzMuNjc1IDE0Ljc2OTIgMy45IDE0LjU3MjMgMy45IDE0LjMxOTJDMy45IDEzLjU4OCA0LjQzNDM4IDEyLjk2OTIgNS4xMzc1IDEyLjk2OTJINi4yNjI1QzYuNDAzMTMgMTIuOTY5MiA2LjU0Mzc1IDEzLjAyNTUgNi42ODQzOCAxMy4wODE3QzYuOTA5MzcgMTIuODI4NiA3LjEzNDM3IDEyLjYzMTcgNy40MTU2MiAxMi40MzQ4WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg=="
            }
        ]

    }

    /** Start monitoring dimension data */
    start() {

        // Set defaults
        this.data = Object.assign({}, this.defaults)

        // Create first time promise
        this.pendingPromise = new Promise(resolve => {
            this.pendingResolve = resolve
        })

        // Start monitoring
        if (this.cancelDimensionListener) this.cancelDimensionListener()
        this.cancelDimensionListener = FirebaseUtils.wrap(firebase.firestore().collection("servers").doc(this.server.serverID).collection("dimensions").doc(this.id).onSnapshot(this.onDimensionUpdated.bind(this), console.warn))

    }

    /** Stop monitoring dimension data */
    stop() {

        // Reset values
        this.id = 'default'
        this.server = null
        this.data = {}
        this.loaded = false

        //Clear listener
        if (this.cancelDimensionListener) this.cancelDimensionListener()

    }


    /** @private Called by Firebase when our dimension document is updated */
    onDimensionUpdated(snapshot) {

        // Store it
        this.data = Object.assign({}, this.defaults, snapshot.data())
        this.loaded = true

        // Notify listeners
        this.emit('updated')

        // Resolve promise
        if (this.pendingResolve) {
            this.pendingResolve(this.data)
            this.pendingResolve = null
            this.pendingPromise = null
        }

    }

    /** Wait for data to be loaded. @returns Promise. */
    waitUntilLoaded() {

        // Check if done
        if (this.loaded)
            return Promise.resolve(this.data)

        // Not done, wait for promise
        return this.pendingPromise

    }

    /** Update fields */
    update(fields) {

        // Modify our local copy immediately
        fields.lastModified = Date.now()
        fields.needsBackup = true
        Object.assign(this.data, fields)

        // Do not update if no server
        if (!this.server) return

        // Do the update
        return firebase.firestore().collection("servers").doc(this.server.serverID).collection("dimensions").doc(this.id).set(fields, { merge: true }).catch(err => console.error(`[Server] Unable to update dimension document:`, err))

    }

}

/**
 * Manages interacting with the global server data.
 */
class ServerAnalytics extends EventEmitter {

    /** Analytics ID */
    id = 'default'

    /** Link back to the Server */
    server = null

    /** Server data */
    data = {}

    /** True if loaded */
    loaded = false

    /** Default values, used if nothing is set on the server */
    defaults = {

        /** Provision Id for session */
        provision_id: "",

        /** Business Id */
        business_id: "",

        /** License data */
        license: {},

        /** Metrics data */
        metrics: {},

        /** Meeting data */
        meeting: {},

    }

    /** Start monitoring dimension data */
    async start() {
        // Create first time promise
        this.pendingPromise = new Promise(resolve => {
            this.pendingResolve = resolve
        })
        // Start monitoring
        if (this.cancelAnalyticsListener) this.cancelAnalyticsListener()
        this.cancelAnalyticsListener = FirebaseUtils.wrap(firebase.firestore().collection("servers").doc(this.server.serverID).collection("analytics").doc(this.id).onSnapshot(this.onAnalyticsUpdated.bind(this), console.warn))

    }

    /** Stop monitoring dimension data */
    stop() {

        // Reset values
        this.id = 'default'
        this.server = null
        this.data = {}
        this.loaded = false

        //Clear listener
        if (this.cancelAnalyticsListener) this.cancelAnalyticsListener()

    }

    /** @private Called by Firebase when our dimension document is updated */
    onAnalyticsUpdated(snapshot) {

        // Store it
        this.data = Object.assign({}, this.defaults, snapshot.data())
        this.loaded = true
        this.presenceSnapshot = null

        // Notify listeners
        this.emit('updated')

        // Resolve promise
        if (this.pendingResolve) {
            this.pendingResolve(this.data)
            this.pendingResolve = null
            this.pendingPromise = null
        }

    }

    /** Wait for data to be loaded. @returns Promise. */
    waitUntilLoaded() {

        // Check if done
        if (this.loaded)
            return Promise.resolve(this.data)

        // Not done, wait for promise
        return this.pendingPromise

    }

    /** Get presence snapshot data. Returns a large array with a stride of 4, the first element is the userID, next elements are x, y, z coords. */
    getPresenceSnapshot() {

        // Use existing
        if (this.presenceSnapshot)
            return this.presenceSnapshot

        // Extract it from the string
        this.presenceSnapshot = (this.data.presence_snapshot || '').split('|')
        for (let i = 0; i < this.presenceSnapshot.length; i += 4) {

            // Parse float values
            this.presenceSnapshot[i + 1] = parseFloat(this.presenceSnapshot[i + 1]) || 0
            this.presenceSnapshot[i + 2] = parseFloat(this.presenceSnapshot[i + 2]) || 0
            this.presenceSnapshot[i + 3] = parseFloat(this.presenceSnapshot[i + 3]) || 0

        }

        // Done
        return this.presenceSnapshot

    }

}

/**
 * Represents the server we are connected to. NOTE: This class will be used everywhere throughout the app,
 * so it should not import any other classes (to prevent an import cycle).
 */
export default new class Server {

    /** Instance ID */
    serverID = null

    /** Dimension ID */
    dimensionID = null

    /** Mixer address */
    mixerAddress = ""

    /** Mixer admin key, if the current user is an admin */
    mixerKey = ""

    /** Vatom Spaces instance ID */
    instanceID = ""

    /** Extra fields passed by the server */
    config = {}

    /** Dimension data */
    dimension = new ServerDimension()

    /** Analytics data */
    analytics = new ServerAnalytics()

    /** `true` if the user has joined already. Will be `false` until the user passes the initial loading screen */
    hasJoined = false

    /** The date the space was created. */
    get dateCreated() {
        return this.config.dateCreated || 0
    }

    /** Setup, called when app loads */
    setup() {

        // Fetch server ID from the URL
        let params = new URLSearchParams(location.search)

        if (!this.serverID)
            this.serverID = params.get('instance') || AppState.serverID

        if (!this.serverID)
            throw new Error('No server ID was found. Please check your URL.')

        // Fetch dimension ID
        this.dimensionID = params.get('d') || 'default'

    }

    /** Called when the user opens the app, to get information about the server. This also logs into Firebase with the user's Vatom access token. */
    async query(vatomincAccessToken, additionalAuth = null) {

        const extra = {}

        // Only attach additional config if we have details about the space
        if (AppState.spaceDetails) {
            extra.authorizeBase = AppState.spaceDetails.isEvent || AppState.redirectFromEvent ? 'events' : 'spaces'
            extra.authorizeID = AppState.redirectFromEvent?.id || AppState.spaceDetails.id
        }

        // Get public server details
        let details = await firebase.functions().httpsCallable('queryServer')(Object.assign({
            id: this.serverID,
            vatomincAccessToken,
            env: Environment.current.id,
            additionalAuth: additionalAuth || AppState.spaceDetails?.additionalAuth || null,
        }, extra))

        // Check if server gave us an access token. It should always give us a token.
        if (!details.data.userToken)
            throw new Error("No user token was provided.")

        // Log in to firebase with the new custom token
        await firebase.auth().signInWithCustomToken(details.data.userToken)

        // Done
        return details.data

    }

    /** Starts all the server listeners */
    async startListeners() {

        // Start monitoring dimension data
        console.debug(`[Server] Space ID = ${this.serverID}, dimension = ${this.dimensionID}`)
        this.dimension.id = this.dimensionID
        this.dimension.server = this
        this.dimension.start()

        // Start monitoring analytiucs data
        this.analytics.server = this
        this.analytics.start()

        // Wait for dimension data to be loaded
        await this.dimension.waitUntilLoaded()

        // Wait for analytics data to be loaded
        await this.analytics.waitUntilLoaded()

    }

    /** Called when the user joins the server. */
    async join() {

        // Get server details
        let details = await firebase.functions().httpsCallable('enterServer')({
            instance: this.serverID,
            dimension: this.dimensionID
        })

        // Store details
        this.instanceID = details.data.instanceID
        this.mixerAddress = details.data.mixerAddress
        this.mixerKey = details.data.mixerKey
        this.config = details.data
        this.hasJoined = true

        // Wait for dimension data to be loaded
        await this.dimension.waitUntilLoaded()

        // Wait for analytics data to be loaded
        await this.analytics.waitUntilLoaded()

        // Return User's firebase document
        return details.data.userDocument

    }

    /** Fetch the latest config from the server */
    async refreshConfig() {

        // Get server details
        let details = await firebase.functions().httpsCallable('enterServer')({
            instance: this.serverID,
            dimension: this.dimensionID
        })

        // Store details
        this.config = details.data

    }

    /** Change the Server dimension */
    async changeInstance(serverID, token) {

        // Remove current dimension data and listeners
        this.dimension.stop()
        this.analytics.stop()

        // Set server details
        this.serverID = serverID
        this.dimensionID = 'default'
        AppState.serverID = serverID
        AppState.dimensionID = 'default'

        // Update state of the app
        const details = await this.fetchSpaceDetails(serverID)
        if (details) {
            AppState.spaceDetails = { ...details }
            AppState.businessID = details.businessId
        }

        // Query new server
        await this.query(token)

        // Join new server
        this.setup()
        return await this.join()

    }

    /** Get backup file for this server */
    async getBackupJSON() {

        // Fetch backup from the server
        return await firebase.functions().httpsCallable('createSpaceBackup')({
            spaceID: AppState.serverID,
        }).then(r => r.data)

    }

    /** Restore backup file onto this server, immediately overwriting all existing data. */
    async restoreBackupJSON(json) {

        // Restore backup to the server
        return await firebase.functions().httpsCallable('restoreSpaceBackup')({
            spaceID: AppState.serverID,
            backup: json,
        }).then(r => r.data)

    }

    /**
     * @private Fetches space details from the Vatom API.
     * @param {string} alias Alias for the space to fetch details from.
     * @returns {object} Details about the given space alias.
     */
    async fetchSpaceDetails(alias) {
        const hostURL = `https://${Environment.current.data.domains[0]}`

        try {
            const response = await fetch(`${Environment.current.data.servers.events}/spaces/${alias}`, {
                method: 'GET',
            })

            // Unsuccessful response
            if (!response || !response.ok) {
                const err = new Error(`Please verify the URL for any errors and consider copying and pasting it instead. If the issue persists, please ensure that the URL begins with "${this.hostURL}".`)
                err.title = 'Page Not Found'
                err.extra = `Received ${response.status} ${response.statusText} when fetching space information.`
                err.buttons = {
                    primary: { text: 'Go Back', action: () => window.location.reload() },
                    secondary: { text: 'Visit the Home Page', action: () => window.open(hostURL, '_self') }
                }
                throw err
            }

            const details = await response.json()

            // No space details have been returned
            if (!details || typeof details != 'object' || Object.keys(details).length < 1) {
                throw new Error('Failed to find details about the space. Please try again later.')
            }

            return details
        } catch (err) {
            if (err.message === 'Failed to fetch') {
                const error = new Error('Failed to find details about the space. Please try again later.')
                error.extra = `An unexpected error occurred when fetching from endpoint "${Environment.current.data.servers.events}/spaces/${alias}".`
                AppState.update(AppStateType.ERROR, error)
                return null
            }

            // Show error
            AppState.update(AppStateType.ERROR, err)
        }

        // Done
        return null

    }

}
